import { FC, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { Center, Loader, Stack, Text } from "@mantine/core";
import { toast } from "react-toastify";

import useGetUser from "@funded-here-interface/common/src/hooks/useGetUser";
import {
  RegtankOnboardingStatusEnum,
  Persona,
  EntityTypeEnum,
} from "@funded-here-interface/common/src/constant/enum";
import { setRegtankRequest } from "@funded-here-interface/common/src/features/auth/authSlice_Investor";
import { roleToPath } from "@funded-here-interface/common/src/Utils/navigation";

import { RootState } from "../../store";
import RegtankResponseLayout from "../../layouts/RegtankResponseLayout/RegtankResponseLayout";
import { useStyle } from "./RegtankResponsePage.styles";

// Constants for UI messages
const MESSAGES = {
  LOADING: "Verifying your identity...",
  KYC_COMPLETE: "e-KYC Completed",
  KYC_COMPLETE_DESC: "Your e-KYC process is complete.",
  KYC_UNSUCCESSFUL: "e-KYC Unsuccessful",
  KYC_UNSUCCESSFUL_DESC:
    "Unfortunately, your e-KYC verification was not successful.",
  IDENTITY_SUCCESS: "Identity Verification Successful",
  IDENTITY_SUCCESS_DESC: "Your identity verification was successful.",
  IDENTITY_SUCCESS_JOURNEY:
    "You are all set to continue your investment journey.",
  IDENTITY_UNSUCCESSFUL: "Identity Verification Unsuccessful",
  IDENTITY_UNSUCCESSFUL_DESC:
    "Unfortunately, your identity verification was not successful.",
  REVIEW_PENDING:
    "We will now review your submitted information and notify you once the review is complete.",
  REVIEW_REJECTED:
    "Our onboarding team is reviewing the matter to identify the issue. Once the review is complete, we will notify you by email and provide guidance on when you may reattempt verification.",
  REVIEW_THANKS: "Thank you for your patience and understanding.",
  CONTINUE_SIGNING:
    "Click the 'Continue' button to proceed with document signing.",
  NO_ACTION: "No action required at this time",
} as const;

const LOADER_COLOR = "#338F86";

// Type for verification state
type VerificationState = {
  isLoading: boolean;
  isRejected: boolean;
  hasPendingOnboarding: boolean;
  showContinueButton: boolean;
};

const RegtankResponsePage: FC = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { classes } = useStyle();
  const { redirectPath } = useParams();
  const { token } = useSelector((state: RootState) => state.auth);
  const { data, refetch } = useGetUser(token);
  const location = useLocation();

  // Consolidated state
  const [verificationState, setVerificationState] = useState<VerificationState>(
    {
      isLoading: true,
      isRejected: false,
      hasPendingOnboarding: false,
      showContinueButton: false,
    }
  );

  /**
   * Navigate user to appropriate page after verification
   */
  const handleContinue = () => {
    if (redirectPath) {
      navigate(`/${redirectPath}`);
    } else {
      const path = roleToPath(data?.user, location.pathname);
      if (path) {
        navigate(path);
      } else {
        toast.warn(MESSAGES.NO_ACTION);
      }
    }
  };

  /**
   * Determine if continue button should be shown based on user role and regtank status
   */
  const shouldShowContinueButton = (
    latestRegtankStatus: RegtankOnboardingStatusEnum,
    role?: Persona,
    entityType?: EntityTypeEnum,
    hasPendingOnboarding?: boolean
  ): boolean => {
    return (
      !hasPendingOnboarding ||
      (latestRegtankStatus === RegtankOnboardingStatusEnum.APPROVED &&
        (role === Persona.AUTH_REP ||
          (role === Persona.INVESTOR &&
            entityType === EntityTypeEnum.INDIVIDUAL)))
    );
  };

  // Initial data fetch
  useEffect(() => {
    refetch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Process user data and Regtank status
  useEffect(() => {
    if (!data?.user) {
      setVerificationState((prev) => ({ ...prev, isLoading: false }));
      return;
    }

    const role = data.user.pendingOnBoardingAs;
    const entityType = data.user.investorCategory?.entityType;
    const hasPendingOnboarding = !!data.user.pendingOnBoardingAs;
    const latestRegtankRequest = data.user.regtankRequest?.[0];

    const status = latestRegtankRequest.onboardingStatus;
    const isBeforeExpiry =
      latestRegtankRequest.expiredDate >= new Date().toISOString();
    // Handle approved status
    if (status === RegtankOnboardingStatusEnum.APPROVED) {
      dispatch(setRegtankRequest({ regtankRequest: latestRegtankRequest }));
      setVerificationState({
        isLoading: false,
        isRejected: false,
        hasPendingOnboarding,
        showContinueButton: shouldShowContinueButton(
          status,
          role,
          entityType,
          hasPendingOnboarding
        ),
      });
      return;
    }

    // Handle rejected status
    if (
      status === RegtankOnboardingStatusEnum.REJECTED ||
      (!isBeforeExpiry &&
        status === RegtankOnboardingStatusEnum.LIVENESS_FAILED)
    ) {
      setVerificationState({
        isLoading: false,
        isRejected: true,
        hasPendingOnboarding,
        showContinueButton: shouldShowContinueButton(
          status,
          role,
          entityType,
          hasPendingOnboarding
        ),
      });
      return;
    }

    // Handle processing/pending status - poll every 5 seconds
    if (
      status === RegtankOnboardingStatusEnum.PROCESSING ||
      status === RegtankOnboardingStatusEnum.URL_GENERATED
    ) {
      setVerificationState((prev) => ({ ...prev, isLoading: true }));
      const interval = setInterval(() => {
        refetch();
      }, 5000);
      return () => clearInterval(interval);
    }

    // Default case
    setVerificationState({
      isLoading: false,
      isRejected: false,
      hasPendingOnboarding,
      showContinueButton: shouldShowContinueButton(
        RegtankOnboardingStatusEnum.PROCESSING,
        role,
        entityType,
        hasPendingOnboarding
      ),
    });
  }, [data, dispatch, refetch]);

  /**
   * Render loading state
   */
  const renderLoadingState = () => (
    <div className={classes.mainContainer}>
      <Center>
        <Stack align="center" spacing="md">
          <Loader size="lg" color={LOADER_COLOR} />
          <Text color="dimmed">{MESSAGES.LOADING}</Text>
        </Stack>
      </Center>
    </div>
  );

  /**
   * Render content for different verification states
   */
  const renderContent = () => {
    const { isRejected, hasPendingOnboarding, showContinueButton } =
      verificationState;

    // Determine heading and description based on state
    let heading: string;
    let description: string[];

    if (hasPendingOnboarding) {
      if (isRejected) {
        heading = MESSAGES.KYC_UNSUCCESSFUL;
        description = [
          MESSAGES.KYC_UNSUCCESSFUL_DESC,
          MESSAGES.REVIEW_REJECTED,
          MESSAGES.REVIEW_THANKS,
        ];
      } else {
        heading = MESSAGES.KYC_COMPLETE;
        description = [
          MESSAGES.KYC_COMPLETE_DESC,
          showContinueButton
            ? MESSAGES.CONTINUE_SIGNING
            : MESSAGES.REVIEW_PENDING,
        ];
      }
    } else {
      if (isRejected) {
        heading = MESSAGES.IDENTITY_UNSUCCESSFUL;
        description = [
          MESSAGES.IDENTITY_UNSUCCESSFUL_DESC,
          MESSAGES.REVIEW_REJECTED,
          MESSAGES.REVIEW_THANKS,
        ];
      } else {
        heading = MESSAGES.IDENTITY_SUCCESS;
        description = [
          MESSAGES.IDENTITY_SUCCESS_DESC,
          MESSAGES.IDENTITY_SUCCESS_JOURNEY,
        ];
      }
    }

    return (
      <div className={classes.mainContainer}>
        <div className={classes.container}>
          <Text className={classes.subHeading}>{heading}</Text>
          {description.map((text, index) => (
            <Text key={index} className={classes.text}>
              {text}
            </Text>
          ))}
        </div>
        {showContinueButton && (
          <button className={classes.proceedButton} onClick={handleContinue}>
            <b>Continue</b>
          </button>
        )}
      </div>
    );
  };

  return (
    <RegtankResponseLayout
      showLogo={false}
      content={
        verificationState.isLoading ? renderLoadingState() : renderContent()
      }
    />
  );
};

export default RegtankResponsePage;
